import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { FileText, Download, Search, Filter, Loader2, Eye, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, LogOut, Lock, ArrowUp, ArrowDown, ArrowUpDown, X, FolderTree, Wrench, Building2, Package, ChevronDown, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default column widths for portal
const DEFAULT_PORTAL_COLUMN_WIDTHS = {
  document_number: 180,
  title: 200,
  discipline: 90,
  revision: 60,
  status: 90,
  review_code: 100,
  date: 90,
  actions: 140
};

// Sort icon component
const SortIconComponent = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
    : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
};

const DocStatPortal = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [portalUser, setPortalUser] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total_documents: 0, disciplines: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [downloading, setDownloading] = useState(null);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('document_number');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_PORTAL_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Tree filter state
  const [treeFilters, setTreeFilters] = useState({
    discipline: null,
    disciplineCode: null,
    companyCode: null,
    projectCode: null
  });
  const [expandedTrees, setExpandedTrees] = useState({
    documents: true,
    discipline: false,
    package: false,
    company: false
  });
  const [uniqueValues, setUniqueValues] = useState({
    disciplines: [],
    disciplineCodes: [],
    companyCodes: [],
    projectCodes: []
  });

  // Check existing auth on mount
  useEffect(() => {
    checkAuth();
  }, []);
  
  // Auto-refresh data every 60 seconds and when tab becomes visible
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    
    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 60000);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated]);

  const checkAuth = async () => {
    const token = localStorage.getItem('portal_token');
    if (token) {
      try {
        const response = await axios.get(`${API}/portal/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPortalUser(response.data);
        setIsAuthenticated(true);
        fetchData(token);
      } catch (error) {
        localStorage.removeItem('portal_token');
        setIsAuthenticated(false);
        setPortalUser(null);
      }
    }
    setCheckingAuth(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/portal/auth/login`, {
        username,
        password
      });
      
      const { access_token, user } = response.data;
      localStorage.setItem('portal_token', access_token);
      
      try {
        const headers = { Authorization: `Bearer ${access_token}` };
        const [docsRes, statsRes] = await Promise.all([
          axios.get(`${API}/portal/documents`, { headers }),
          axios.get(`${API}/portal/stats`, { headers })
        ]);
        
        const docs = docsRes.data;
        setDocuments(docs);
        setStats(statsRes.data);
        
        // Extract unique values for tree filters
        const disciplines = [...new Set(docs.map(d => d.discipline).filter(Boolean))];
        const disciplineCodes = [...new Set(docs.map(d => d.discipline_code).filter(Boolean))];
        const companyCodes = [...new Set(docs.map(d => d.company_code).filter(Boolean))];
        const projectCodes = [...new Set(docs.map(d => d.project_code).filter(Boolean))];
        
        setUniqueValues({
          disciplines: disciplines.sort(),
          disciplineCodes: disciplineCodes.sort(),
          companyCodes: companyCodes.sort(),
          projectCodes: projectCodes.sort()
        });
        
        setPortalUser(user);
        setIsAuthenticated(true);
        setLoading(false);
        toast.success('Welcome to DocStat Portal');
      } catch (fetchError) {
        console.error('Error fetching data:', fetchError);
        setPortalUser(user);
        setIsAuthenticated(true);
        toast.success('Welcome to DocStat Portal');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    setIsAuthenticated(false);
    setPortalUser(null);
    setDocuments([]);
    setUsername('');
    setPassword('');
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('portal_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async (token) => {
    try {
      setLoading(true);
      const authToken = token || localStorage.getItem('portal_token');
      if (!authToken) return;
      
      const headers = { 
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
      
      const timestamp = Date.now();
      
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/portal/documents?_t=${timestamp}`, { headers }),
        axios.get(`${API}/portal/stats?_t=${timestamp}`, { headers })
      ]);
      
      const docs = docsRes.data;
      setDocuments(docs);
      setStats(statsRes.data);
      
      // Extract unique values for tree filters
      const disciplines = [...new Set(docs.map(d => d.discipline).filter(Boolean))];
      const disciplineCodes = [...new Set(docs.map(d => d.discipline_code).filter(Boolean))];
      const companyCodes = [...new Set(docs.map(d => d.company_code).filter(Boolean))];
      const projectCodes = [...new Set(docs.map(d => d.project_code).filter(Boolean))];
      
      setUniqueValues({
        disciplines: disciplines.sort(),
        disciplineCodes: disciplineCodes.sort(),
        companyCodes: companyCodes.sort(),
        projectCodes: projectCodes.sort()
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const getReviewCodeBadge = (code, label) => {
    if (!code) return null;
    const colors = {
      'code_1': 'bg-green-100 text-green-800',
      'code_2': 'bg-yellow-100 text-yellow-800',
      'code_3': 'bg-red-100 text-red-800',
      'code_4': 'bg-blue-100 text-blue-800'
    };
    return (
      <Badge className={`${colors[code] || 'bg-gray-100 text-gray-800'} text-xs font-medium`}>
        {label || code}
      </Badge>
    );
  };

  const handlePreview = async (doc) => {
    try {
      setPdfLoading(true);
      setPreviewDoc(doc);
      setPreviewOpen(true);
      setCurrentPage(1);
      setScale(1.0);
      
      const response = await axios.get(`${API}/portal/documents/${doc.id}/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDoc(null);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setNumPages(null);
    setCurrentPage(1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleDownload = async (doc) => {
    try {
      setDownloading(doc.id);
      const response = await axios.get(`${API}/portal/documents/${doc.id}/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = doc.title || doc.name || doc.document_number || 'document';
      link.setAttribute('download', fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(String(dateStr).endsWith('Z') || String(dateStr).includes('+') ? dateStr : dateStr + 'Z').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(60, startWidthRef.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Tree filter handlers
  const handleTreeFilterClick = (filterType, value) => {
    setTreeFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? null : value
    }));
  };

  const toggleTree = (treeName) => {
    setExpandedTrees(prev => ({
      ...prev,
      [treeName]: !prev[treeName]
    }));
  };

  const clearAllTreeFilters = () => {
    setTreeFilters({
      discipline: null,
      disciplineCode: null,
      companyCode: null,
      projectCode: null
    });
  };

  const hasActiveTreeFilters = Object.values(treeFilters).some(v => v !== null);

  // Filter and sort documents
  const filteredAndSortedDocuments = useMemo(() => {
    let result = documents.filter(doc => {
      const matchesSearch = !searchTerm || 
        doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.document_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDiscipline = selectedDiscipline === 'all' || 
        doc.discipline === selectedDiscipline;
      
      const matchesTreeDiscipline = !treeFilters.discipline || doc.discipline === treeFilters.discipline;
      const matchesTreeDisciplineCode = !treeFilters.disciplineCode || doc.discipline_code === treeFilters.disciplineCode;
      const matchesTreeCompanyCode = !treeFilters.companyCode || doc.company_code === treeFilters.companyCode;
      const matchesTreeProjectCode = !treeFilters.projectCode || doc.project_code === treeFilters.projectCode;
      
      return matchesSearch && matchesDiscipline && matchesTreeDiscipline && matchesTreeDisciplineCode && matchesTreeCompanyCode && matchesTreeProjectCode;
    });

    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'document_number':
          aVal = (a.document_number || '').toLowerCase();
          bVal = (b.document_number || '').toLowerCase();
          break;
        case 'title':
          aVal = (a.title || a.name || '').toLowerCase();
          bVal = (b.title || b.name || '').toLowerCase();
          break;
        case 'discipline':
          aVal = (a.discipline || '').toLowerCase();
          bVal = (b.discipline || '').toLowerCase();
          break;
        case 'revision':
          aVal = (a.revision || 'A').toLowerCase();
          bVal = (b.revision || 'A').toLowerCase();
          break;
        case 'date':
          aVal = new Date(a.revision_date || a.updated_at || 0).getTime();
          bVal = new Date(b.revision_date || b.updated_at || 0).getTime();
          break;
        default:
          aVal = '';
          bVal = '';
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [documents, searchTerm, selectedDiscipline, treeFilters, sortColumn, sortDirection]);

  const disciplines = [...new Set(documents.map(d => d.discipline).filter(Boolean))];

  // Loading state
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Login Page
  if (!isAuthenticated) {
    return (
      <div key="login-page" className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <header className="py-6 px-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-900 rounded-xl flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-gray-900">DocStat Portal</span>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                <Lock className="h-8 w-8 text-blue-900" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Sign In</h1>
              <p className="text-gray-500 text-sm">Enter your credentials to access documents</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 bg-gray-50 border-gray-200 rounded-xl text-base"
                  required
                  data-testid="portal-username"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-gray-50 border-gray-200 rounded-xl text-base"
                  required
                  data-testid="portal-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-base font-medium"
                data-testid="portal-login-btn"
              >
                {loginLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="text-center text-gray-400 text-xs mt-8">
              Contact your administrator if you need access
            </p>
          </div>
        </div>

        <footer className="py-6 text-center">
          <p className="text-gray-400 text-sm">© {new Date().getFullYear()} DocStat</p>
        </footer>
      </div>
    );
  }

  // Main Portal View - Documents Only (View/Download)
  return (
    <div key="portal-view" className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-900 rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">DocStat Portal</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{portalUser?.username}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-2xl font-semibold text-gray-900">{stats.total_documents}</span>
              <span className="text-gray-500 text-sm ml-2">Documents</span>
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <span className="text-2xl font-semibold text-gray-900">{disciplines.length}</span>
              <span className="text-gray-500 text-sm ml-2">Disciplines</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex max-w-7xl mx-auto">
        {/* Left Sidebar - Filter Trees */}
        <div className="w-64 border-r border-gray-200 bg-white min-h-[calc(100vh-180px)] hidden lg:block">
          <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Filter Trees</h3>
            {hasActiveTreeFilters && (
              <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={clearAllTreeFilters}>
                Clear
              </Button>
            )}
          </div>
          
          {/* Documents Tree */}
          <div className="border-b border-gray-100">
            <div 
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleTree('documents')}
            >
              {expandedTrees.documents ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
              <FolderTree className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium flex-1">Documents Tree</span>
              {uniqueValues.disciplines.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded">{uniqueValues.disciplines.length}</span>
              )}
            </div>
            {expandedTrees.documents && (
              <div className="pb-2 pl-6">
                {uniqueValues.disciplines.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1 px-2">No items</p>
                ) : (
                  uniqueValues.disciplines.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer rounded-sm text-sm hover:bg-gray-100 ${
                        treeFilters.discipline === item ? 'bg-blue-50 text-blue-700 font-medium' : ''
                      }`}
                      onClick={() => handleTreeFilterClick('discipline', item)}
                    >
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Discipline Tree */}
          <div className="border-b border-gray-100">
            <div 
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleTree('discipline')}
            >
              {expandedTrees.discipline ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
              <Wrench className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium flex-1">Discipline Tree</span>
              {uniqueValues.disciplineCodes.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded">{uniqueValues.disciplineCodes.length}</span>
              )}
            </div>
            {expandedTrees.discipline && (
              <div className="pb-2 pl-6">
                {uniqueValues.disciplineCodes.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1 px-2">No items</p>
                ) : (
                  uniqueValues.disciplineCodes.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer rounded-sm text-sm hover:bg-gray-100 ${
                        treeFilters.disciplineCode === item ? 'bg-blue-50 text-blue-700 font-medium' : ''
                      }`}
                      onClick={() => handleTreeFilterClick('disciplineCode', item)}
                    >
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Package Tree */}
          <div className="border-b border-gray-100">
            <div 
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleTree('package')}
            >
              {expandedTrees.package ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
              <Package className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium flex-1">Package Tree</span>
              {uniqueValues.projectCodes.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded">{uniqueValues.projectCodes.length}</span>
              )}
            </div>
            {expandedTrees.package && (
              <div className="pb-2 pl-6">
                {uniqueValues.projectCodes.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1 px-2">No items</p>
                ) : (
                  uniqueValues.projectCodes.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer rounded-sm text-sm hover:bg-gray-100 ${
                        treeFilters.projectCode === item ? 'bg-blue-50 text-blue-700 font-medium' : ''
                      }`}
                      onClick={() => handleTreeFilterClick('projectCode', item)}
                    >
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Company Tree */}
          <div className="border-b border-gray-100">
            <div 
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleTree('company')}
            >
              {expandedTrees.company ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
              <Building2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium flex-1">Company Tree</span>
              {uniqueValues.companyCodes.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded">{uniqueValues.companyCodes.length}</span>
              )}
            </div>
            {expandedTrees.company && (
              <div className="pb-2 pl-6">
                {uniqueValues.companyCodes.length === 0 ? (
                  <p className="text-xs text-gray-400 py-1 px-2">No items</p>
                ) : (
                  uniqueValues.companyCodes.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer rounded-sm text-sm hover:bg-gray-100 ${
                        treeFilters.companyCode === item ? 'bg-blue-50 text-blue-700 font-medium' : ''
                      }`}
                      onClick={() => handleTreeFilterClick('companyCode', item)}
                    >
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                      <span className="truncate">{item}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Main Content Area */}
        <main className="flex-1 px-6 py-6">
          {/* Active Tree Filters */}
          {hasActiveTreeFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {treeFilters.discipline && (
                <Badge variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-700">
                  Discipline: {treeFilters.discipline}
                  <button onClick={() => handleTreeFilterClick('discipline', treeFilters.discipline)} className="hover:text-blue-900">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {treeFilters.disciplineCode && (
                <Badge variant="secondary" className="flex items-center gap-1 bg-orange-100 text-orange-700">
                  Code: {treeFilters.disciplineCode}
                  <button onClick={() => handleTreeFilterClick('disciplineCode', treeFilters.disciplineCode)} className="hover:text-orange-900">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {treeFilters.projectCode && (
                <Badge variant="secondary" className="flex items-center gap-1 bg-purple-100 text-purple-700">
                  Package: {treeFilters.projectCode}
                  <button onClick={() => handleTreeFilterClick('projectCode', treeFilters.projectCode)} className="hover:text-purple-900">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {treeFilters.companyCode && (
                <Badge variant="secondary" className="flex items-center gap-1 bg-green-100 text-green-700">
                  Company: {treeFilters.companyCode}
                  <button onClick={() => handleTreeFilterClick('companyCode', treeFilters.companyCode)} className="hover:text-green-900">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 bg-white border-gray-200 rounded-lg"
                data-testid="portal-search"
              />
            </div>
            <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
              <SelectTrigger className="w-full sm:w-48 h-10 bg-white border-gray-200 rounded-lg">
                <Filter className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue placeholder="All Disciplines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Disciplines</SelectItem>
                {disciplines.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          {(searchTerm || selectedDiscipline !== 'all' || hasActiveTreeFilters || filteredAndSortedDocuments.length !== documents.length) && (
            <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
              <span>
                <span className="font-medium">{filteredAndSortedDocuments.length}</span> of <span className="font-medium">{documents.length}</span> documents
              </span>
              {(searchTerm || selectedDiscipline !== 'all' || hasActiveTreeFilters) && (
                <button 
                  onClick={() => { setSearchTerm(''); setSelectedDiscipline('all'); clearAllTreeFilters(); }}
                  className="text-primary hover:underline text-xs"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Documents List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredAndSortedDocuments.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No documents found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              {/* Table Header */}
              <div className="overflow-x-auto">
                <div className="min-w-[900px]">
                  <div className="flex items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.document_number, minWidth: columnWidths.document_number }}
                    >
                      <button 
                        onClick={() => handleSort('document_number')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        Document No.
                        <SortIconComponent column="document_number" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('document_number', e)}
                      />
                    </div>
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.title, minWidth: columnWidths.title }}
                    >
                      <button 
                        onClick={() => handleSort('title')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        Title
                        <SortIconComponent column="title" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('title', e)}
                      />
                    </div>
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.discipline, minWidth: columnWidths.discipline }}
                    >
                      <button 
                        onClick={() => handleSort('discipline')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        Discipline
                        <SortIconComponent column="discipline" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('discipline', e)}
                      />
                    </div>
                    <div 
                      className="relative group text-center"
                      style={{ width: columnWidths.revision, minWidth: columnWidths.revision }}
                    >
                      <button 
                        onClick={() => handleSort('revision')}
                        className="flex items-center justify-center gap-1 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors w-full"
                      >
                        Rev
                        <SortIconComponent column="revision" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('revision', e)}
                      />
                    </div>
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.status, minWidth: columnWidths.status }}
                    >
                      <span className="text-sm font-semibold text-gray-700">Status</span>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('status', e)}
                      />
                    </div>
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.review_code, minWidth: columnWidths.review_code }}
                    >
                      <span className="text-sm font-semibold text-gray-700">Review Code</span>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('review_code', e)}
                      />
                    </div>
                    <div 
                      className="relative group"
                      style={{ width: columnWidths.date, minWidth: columnWidths.date }}
                    >
                      <button 
                        onClick={() => handleSort('date')}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                      >
                        Date
                        <SortIconComponent column="date" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                        onMouseDown={(e) => startResize('date', e)}
                      />
                    </div>
                    <div 
                      className="text-center text-sm font-semibold text-gray-700"
                      style={{ width: columnWidths.actions, minWidth: columnWidths.actions }}
                    >
                      Actions
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Document Rows */}
              <div className="divide-y divide-gray-100 overflow-x-auto">
                {filteredAndSortedDocuments.map((doc) => (
                  <div 
                    key={doc.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors min-w-[900px]"
                    data-testid={`portal-doc-${doc.id}`}
                  >
                    <div style={{ width: columnWidths.document_number, minWidth: columnWidths.document_number }}>
                      <span className="font-mono text-sm text-blue-900 font-medium">
                        {doc.document_number || '-'}
                      </span>
                    </div>
                    
                    <div style={{ width: columnWidths.title, minWidth: columnWidths.title }}>
                      <p className="text-sm text-gray-900 truncate font-medium" title={doc.title || doc.name}>
                        {doc.title || doc.name || '-'}
                      </p>
                      {doc.description && (
                        <p className="text-xs text-gray-500 truncate">{doc.description}</p>
                      )}
                    </div>
                    
                    <div style={{ width: columnWidths.discipline, minWidth: columnWidths.discipline }}>
                      {doc.discipline ? (
                        <Badge variant="secondary" className="font-normal text-xs bg-gray-100 text-gray-600 hover:bg-gray-100">
                          {doc.discipline}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                    
                    <div className="text-center" style={{ width: columnWidths.revision, minWidth: columnWidths.revision }}>
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gray-100 text-xs font-mono font-medium text-gray-700">
                        {doc.revision || 'A'}
                      </span>
                    </div>
                    
                    <div style={{ width: columnWidths.status, minWidth: columnWidths.status }}>
                      {(() => {
                        const status = doc.status || 'draft';
                        const statusColors = {
                          'open': 'bg-blue-100 text-blue-700',
                          'in_progress': 'bg-yellow-100 text-yellow-700',
                          'in_review': 'bg-yellow-100 text-yellow-700',
                          'approved': 'bg-green-100 text-green-700',
                          'closed': 'bg-green-100 text-green-700',
                          'rejected': 'bg-red-100 text-red-700',
                          'draft': 'bg-gray-100 text-gray-600'
                        };
                        const statusLabels = {
                          'open': 'Open',
                          'in_progress': 'In Progress',
                          'in_review': 'In Review',
                          'approved': 'Approved',
                          'closed': 'Closed',
                          'rejected': 'Rejected',
                          'draft': 'Draft'
                        };
                        return (
                          <span className={`text-xs px-2 py-1 rounded-md font-medium ${statusColors[status] || statusColors['draft']}`}>
                            {statusLabels[status] || status}
                          </span>
                        );
                      })()}
                    </div>
                    
                    <div style={{ width: columnWidths.review_code, minWidth: columnWidths.review_code }}>
                      {doc.review_code ? getReviewCodeBadge(doc.review_code, doc.review_code_label) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-500" style={{ width: columnWidths.date, minWidth: columnWidths.date }}>
                      {formatDate(doc.revision_date || doc.updated_at)}
                    </div>
                    
                    <div className="flex justify-center gap-1" style={{ width: columnWidths.actions, minWidth: columnWidths.actions }}>
                      <button
                        onClick={() => handlePreview(doc)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Preview"
                        data-testid={`preview-${doc.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        disabled={downloading === doc.id}
                        className="p-2 text-gray-400 hover:text-primary hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download"
                        data-testid={`download-${doc.id}`}
                      >
                        {downloading === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {previewDoc?.title || previewDoc?.name || 'Document Preview'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* PDF Toolbar */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {numPages || '?'}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.min(numPages || 1, currentPage + 1))}
                  disabled={currentPage >= (numPages || 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setScale(Math.max(0.5, scale - 0.2))}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setScale(Math.min(2.5, scale + 0.2))}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => previewDoc && handleDownload(previewDoc)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : pdfUrl ? (
                <div className="flex justify-center">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-64">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      </div>
                    }
                    error={
                      <div className="text-center py-8 text-red-500">
                        Failed to load PDF
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={currentPage} 
                      scale={scale}
                      className="shadow-lg"
                    />
                  </Document>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No PDF to display
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocStatPortal;
